import { CommonModule } from '@angular/common';
import { Component, computed, OnInit, Renderer2 } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { OktaService } from '../../shared/services/okta.service';
import {
  requestActivateFactorPlayload,
  requestEnrollFactorPlayload,
  requestforEmulationPayload,
  requestRemoveUserFactorPayload,
} from '../../shared/models/model';
import { FactorType } from '../../shared/models/enum';
import { EmulationService } from '../../shared/services/emulation.service';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
})
export class UserProfileComponent implements OnInit {
  userForm: FormGroup = new FormGroup({});
  errorMessage: string = '';
  showphoneotpverifybtn: boolean = false;
  showaudiootpverifybtn: boolean = false;
  showemailotpverifybtn: boolean = false;
  showPhoneVerifySection: boolean = false;
  showAudioVerifySection: boolean = false;
  showEmailVerifySection: boolean = false;
  controlNames = ['email', 'text', 'audio'];
  IsVerifiedBtnEnabled: boolean[] = [false, false, false];
  IsOTPVerifiedBtnEnabled: boolean[] = [false, false, false];
  validationPending: boolean[] = [false, false, false];
  notificationText: string = '';
  notificationVisible: boolean = false;
  notificationStyle: string = '';
  userInfo: any;
  InitialUserInfo: any;
  isEmailVerified: boolean = false;
  isPhoneVerified: boolean = false;
  isAudioVerified: boolean = false;
  isPhoneChanged: boolean = false;
  isEmailChanged: boolean = false;
  isAudioChanged: boolean = false;
  emulatedUser = computed(() => this._emulationService.emulatedUser());

  constructor(
    private _okta: OktaService,
    private _renderer: Renderer2,
    private _emulationService: EmulationService,
  ) {}

  ngOnInit(): void {
    this.userForm = new FormGroup({
      firstname: new FormControl({ value: '', disabled: true }, [Validators.required]),
      lastname: new FormControl({ value: '', disabled: true }, [Validators.required]),
      email: new FormControl({ value: '', disabled: this.emulatedUser()?.userId ? true : false }, [
        Validators.required,
        this.emailValidator,
      ]),
      phone: new FormControl({ value: '', disabled: this.emulatedUser()?.userId ? true : false }, [
        Validators.required,
        Validators.pattern('^[+]?[0-9]{10,11}$'),
      ]),
      audio: new FormControl({ value: '', disabled: this.emulatedUser()?.userId ? true : false }, [
        Validators.required,
        Validators.pattern('^[+]?[0-9]{10,11}$'),
      ]),
      phoneotp: new FormControl('', [Validators.maxLength(6), Validators.pattern('^[0-9]{0,6}$')]),
      audiootp: new FormControl('', [Validators.maxLength(6), Validators.pattern('^[0-9]{0,6}$')]),
      emailotp: new FormControl('', [Validators.maxLength(6), Validators.pattern('^[0-9]{0,6}$')]),
    });
    this.getUser();
  }

  emailValidator(control: AbstractControl): ValidationErrors | null {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const valid = emailRegex.test(control.value) || control.value === '';
    return valid ? null : { invalidEmail: true };
  }

  getUser() {
    const payload: requestforEmulationPayload = {
      requestingSystem: 'FinPro',
      isEmulating: this.emulatedUser()?.userId ? true : false,
      emulatingUserId: this.emulatedUser()?.userId ?? '',
    };
    this._okta.getUserProfile(payload)?.subscribe((_data) => {
      if (_data) {
        this.userInfo = this._okta.userData;
        this.InitialUserInfo = this.userInfo;
        this.userForm.patchValue({
          firstname: this.userInfo.firstName,
          lastname: this.userInfo.lastName,
          email: this._okta.getFactorValue(this.userInfo, FactorType.Email),
          phone: this._okta.getFactorValue(this.userInfo, FactorType.Phone),
          audio: this._okta.getFactorValue(this.userInfo, FactorType.Audio),
        });
        this.resetVerifications();
      }
    });
  }

  resetVerifications() {
    this.isEmailVerified =
      this._okta.getFactorValue(this.userInfo, FactorType.Email) !== '' &&
      this.validationPending[FactorType.Email] === false;
    this.isPhoneVerified =
      this._okta.getFactorValue(this.userInfo, FactorType.Phone) !== '' &&
      this.validationPending[FactorType.Phone] === false;
    this.isAudioVerified =
      this._okta.getFactorValue(this.userInfo, FactorType.Audio) !== '' &&
      this.validationPending[FactorType.Audio] === false;
    this.isPhoneChanged = this.isEmailChanged = this.isAudioChanged = false;
    this.showPhoneVerifySection = this.showEmailVerifySection = this.showAudioVerifySection = false;
  }

  get formControls() {
    return this.userForm.controls;
  }

  inputOnChange(event: any) {
    const controlName = event?.getAttribute('formControlName');
    if (controlName === 'phone') {
      this.isPhoneChanged =
        this.userForm.controls[controlName].value !== this._okta.getFactorValue(this.InitialUserInfo, FactorType.Phone);
      if (this.isPhoneChanged) {
        this.isPhoneVerified = false;
      } else {
        this.resetVerifications();
      }
    } else if (controlName === 'email') {
      this.isEmailChanged =
        this.userForm.controls[controlName].value !== this._okta.getFactorValue(this.InitialUserInfo, FactorType.Email);
      if (this.isEmailChanged) {
        this.isEmailVerified = false;
      } else {
        this.resetVerifications();
      }
    } else if (controlName === 'audio') {
      this.isAudioChanged =
        this.userForm.controls[controlName].value !== this._okta.getFactorValue(this.InitialUserInfo, FactorType.Audio);
      if (this.isAudioChanged) {
        this.isAudioVerified = false;
      } else {
        this.resetVerifications();
      }
    }
  }

  validateOtpInput(event: any) {
    const target = event.target;
    if (target.value !== '') {
      switch (target.id) {
        case 'audiootp':
          this.showaudiootpverifybtn = true && this.userForm.controls[target.id].valid;
          break;
        case 'phoneotp':
          this.showphoneotpverifybtn = true && this.userForm.controls[target.id].valid;
          break;
        case 'emailotp':
          this.showemailotpverifybtn = true && this.userForm.controls[target.id].valid;
          break;
        default:
          break;
      }
    } else {
      this.showaudiootpverifybtn = false;
      this.showphoneotpverifybtn = false;
      this.showemailotpverifybtn = false;
    }
  }

  showVerifySection(type: string) {
    const payload = this.createEnrollFactorPayload(type);
    this.disableBtn(type, true);
    this._okta.enrollFactorApi(payload)?.subscribe({
      next: (_data) => {
        if (_data) {
          this.controlDynamicInputs(type, true);
          // this.disableBtn(type, false);
        }
      },
      error: (e) => {
        this.disableBtn(type, false);
        this.handleNotifications('apiError', e.error?.response ? e.error.response : '');
      },
    });
  }

  validateOtp(type: any) {
    const payload = this.createActivateFactorPayload(type);
    this.disableOTPBtn(type, true);
    this._okta.activateFactorFactorApi(payload)?.subscribe({
      next: (_data) => {
        if (_data) {
          this.controlDynamicInputs(type, false);
          this.disableOTPBtn(type, false);
          this.getUser();
          this.handleNotifications('apiSuccess');
        }
      },
      error: (e) => {
        this.disableOTPBtn(type, false);
        this.handleNotifications('apiError', e.error?.response ? e.error.response : '');
      },
    });
  }

  resendOTP(type: any) {
    const payload = this.createEnrollFactorPayload(type);
    this._okta.resendFactor(payload)?.subscribe({
      next: (_data) => {},
      error: (e) => {
        this.handleNotifications('apiError', e.error?.response ? e.error.response : '');
      },
    });
  }

  onRemoveUserFactor($event: Event, index: number) {
    let factorType: number;
    if (index === 0) {
      factorType = FactorType.Phone;
    } else if (index === 1) {
      factorType = FactorType.Audio;
    } else if (index === 2) {
      factorType = FactorType.Email;
    } else {
      throw new Error('Invalid index');
    }

    const payload: requestRemoveUserFactorPayload = {
      requestingSystem: 'FinPro',
      userName: '',
      factorType: factorType,
    };
    this._okta.removeUserFactor(payload)?.subscribe({
      next: (_data) => {
        if (_data) {
          this.getUser();
          this.handleNotifications('apiSuccess');
        }
      },
      error: (_e) => {
        this.handleNotifications('apiError', _e.error?.response ? _e.error.response : '');
        $event.target ? (($event.target as HTMLInputElement).checked = true) : '';
      },
    });
  }

  checkMFACount($event: Event, index: number) {
    if (this.getMFACount(this.userInfo) <= 1) {
      $event.preventDefault();
      this.handleNotifications('checkboxError');
    } else {
      this.onRemoveUserFactor($event, index);
    }
  }

  getMFACount(userInfo: any) {
    return userInfo.factors.filter((d: { status: number }) => d.status === 1).length;
  }

  factorType(type: string) {
    switch (type) {
      case 'email':
        return FactorType.Email;
      case 'phone':
        return FactorType.Phone;
      case 'audio':
        return FactorType.Audio;
      default:
        throw new Error('Invalid control name');
    }
  }

  createEnrollFactorPayload(index: string): requestEnrollFactorPlayload {
    const controlValue = this.userForm.get(index)?.value;
    const factorType: number = this.factorType(index);
    return {
      requestingSystem: 'FinPro',
      factorValue: controlValue,
      factorType: factorType,
    };
  }

  createActivateFactorPayload(index: string): requestActivateFactorPlayload {
    const controlValue = this.userForm.get(index + 'otp')?.value;
    const factorType: number = this.factorType(index);
    return {
      requestingSystem: 'FinPro',
      passCode: controlValue,
      factorType: factorType,
      browserName: this._okta.getBrowserName(),
    };
  }

  onSubmit() {}

  showNotification(text: string, style: string) {
    this.notificationText = text;
    this.notificationStyle = style;
    this.notificationVisible = true;
    // eslint-disable-next-line no-undef
    setTimeout(() => {
      this.notificationVisible = false;
    }, 8000);
  }

  /* Common function to handle different notifications */
  handleNotifications(_notificationType: string, errorMsg = '') {
    if (_notificationType === 'checkboxError') {
      this.showNotification('You must have at least one factor.', 'error');
    } else if (_notificationType === 'apiSuccess') {
      this.showNotification('Your factor has been saved.', 'success');
    } else if (_notificationType === 'apiError') {
      this.showNotification(
        errorMsg === '' || errorMsg.startsWith('Failure-TraceId')
          ? 'Something went wrong. Your request was not fulfilled.'
          : errorMsg,
        'error',
      );
    } else {
      this.notificationVisible = false;
    }

    const targetElement = this._renderer.selectRootElement('#userProfile', true);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  controlDynamicInputs(type: string, flag: boolean) {
    switch (type) {
      case 'phone':
        this.showPhoneVerifySection = flag;
        this.validationPending[FactorType.Phone] = flag;
        this.showphoneotpverifybtn = false;
        break;
      case 'audio':
        this.showAudioVerifySection = flag;
        this.validationPending[FactorType.Audio] = flag;
        this.showaudiootpverifybtn = false;
        break;
      case 'email':
        this.showEmailVerifySection = flag;
        this.validationPending[FactorType.Email] = flag;
        this.showemailotpverifybtn = false;
        break;
      default:
        this.showPhoneVerifySection = false;
        this.showAudioVerifySection = false;
        this.showEmailVerifySection = false;
        break;
    }
    this.userForm.controls[type + 'otp'].reset();
    this.disableBtn(type, false);
  }

  disableBtn(type: string, flag: boolean) {
    switch (type) {
      case 'phone':
        this.IsVerifiedBtnEnabled[0] = flag;
        break;
      case 'audio':
        this.IsVerifiedBtnEnabled[1] = flag;
        break;
      case 'email':
        this.IsVerifiedBtnEnabled[2] = flag;
        break;
      default:
        break;
    }
  }

  disableOTPBtn(type: string, flag: boolean) {
    switch (type) {
      case 'phone':
        this.IsOTPVerifiedBtnEnabled[0] = flag;
        break;
      case 'audio':
        this.IsOTPVerifiedBtnEnabled[1] = flag;
        break;
      case 'email':
        this.IsOTPVerifiedBtnEnabled[2] = flag;
        break;
      default:
        break;
    }
  }

  showVerifyButton(type: string) {
    let returnVal: boolean = false;
    if (type === 'email') {
      returnVal =
        (this.isEmailChanged || this.validationPending[FactorType.Email]) &&
        !this.showEmailVerifySection &&
        !this.isEmailVerified &&
        this.userForm.controls[type].valid;
    } else if (type === 'phone') {
      returnVal =
        (this.isPhoneChanged || this.validationPending[FactorType.Phone]) &&
        !this.showPhoneVerifySection &&
        !this.isPhoneVerified &&
        this.userForm.controls[type].valid;
    } else if (type === 'audio') {
      returnVal =
        (this.isAudioChanged || this.validationPending[FactorType.Audio]) &&
        !this.showAudioVerifySection &&
        !this.isAudioVerified &&
        this.userForm.controls[type].valid;
    }
    return returnVal;
  }
}
